import { useEffect } from "react";
import { products } from "@/data/products";
import { articles, resourcesSeo } from "@/data/resources";

export const SITE_URL = "https://banetech.net";

export type PageSeo = { title: string; description: string; path: string; image?: string };

export const DEFAULT_OG_IMAGE = `${SITE_URL}/images/og-preview.jpg`;
export const ogImageFor = (seo: PageSeo) => seo.image ?? DEFAULT_OG_IMAGE;

export const homeSeo: PageSeo = {
  title: "Enterprise Software & Integration Platform | Bane Tech",
  description:
    "Enterprise integration platform: real-time data integration, CDC, monitoring & observability. FireFly, Lemur Monitor, Bee Portfolio, Gaia Entity, FireAnt, Koala Flow.",
  path: "/",
};

export const productPath = (slug: string) => `/products/${slug}/`;

export const seoForPath = (url: string): PageSeo => {
  const m = url.match(/^\/products\/([^/]+)\/?/);
  const p = m && products.find((x) => x.slug === m[1]);
  if (p) return { title: p.seoTitle, description: p.metaDescription, path: productPath(p.slug), image: `${SITE_URL}/images/products/${p.slug}.png` };
  if (/^\/resources\/?$/.test(url)) return { ...resourcesSeo, path: "/resources/" };
  const r = url.match(/^\/resources\/([^/]+)\/?/);
  const a = r && articles.find((x) => x.slug === r[1]);
  if (a) return { title: a.seoTitle, description: a.metaDescription, path: `/resources/${a.slug}/` };
  return homeSeo;
};

const setMeta = (attr: "name" | "property", key: string, content: string) => {
  let el = document.head.querySelector<HTMLMetaElement>(`meta[${attr}="${key}"]`);
  if (!el) {
    el = document.createElement("meta");
    el.setAttribute(attr, key);
    document.head.appendChild(el);
  }
  el.setAttribute("content", content);
};

/** Keeps the document head in sync on the client (dev preview and in-app navigation). */
export const useSeo = (seo: PageSeo) => {
  useEffect(() => {
    document.title = seo.title;
    setMeta("name", "description", seo.description);
    setMeta("property", "og:title", seo.title);
    setMeta("property", "og:description", seo.description);
    setMeta("property", "og:url", SITE_URL + seo.path);
    setMeta("property", "og:image", ogImageFor(seo));
    setMeta("name", "twitter:image", ogImageFor(seo));
    let link = document.head.querySelector<HTMLLinkElement>('link[rel="canonical"]');
    if (!link) {
      link = document.createElement("link");
      link.rel = "canonical";
      document.head.appendChild(link);
    }
    link.href = SITE_URL + seo.path;
  }, [seo]);
};
